import { useRenderedBootHumanWait } from '#~/routes/boot-overlay/boot-budget-view.js';
/**
 * Post-auth prequalification step (ENG-4175).
 *
 * The signup segmentation wizard was RELOCATED here from the pre-auth /register
 * page. It renders immediately BEFORE the download/add-camera setup step for
 * first-login-ever web users: use-root-bootstrap intercepts the post-login
 * auto-redirect and routes a gated first-login user here before the download
 * flow. This component reuses <PrequalFlow> as-is and owns the analytics +
 * person-property persistence.
 *
 * NON-BLOCKING by design (post-auth placement was chosen to add NO signup
 * friction): the shown-once backstop is set on MOUNT (when the wizard is
 * prompted), so a user who skips or abandons — and any later bootstrap — falls
 * through the gate to the download step instead of being re-prompted. On complete
 * OR skip we return to '/', where the now-prompted gate skips and the bootstrap
 * resolves the web destination (agent/setup → download).
 */

import { EVENT_CATEGORIES, EVENT_NAMES, useAnalytics } from '#~/analytics/index.js';
import { computeQualificationBucket } from '#~/analytics/utils/qualification-mapper.js';
import { useDepot } from '#~/depot/hooks.js';
import { PostHogManager } from '#~/posthog/index.js';
import { PrequalFlow } from '#~/routes/register/prequal-questions.js';
import { clearPrequalResponses, loadPrequalResponses } from '#~/routes/register/prequal-storage.js';
import type { PrequalificationAnswers } from '#~/routes/register/prequal-storage.js';
import { markPrequalPrompted } from '#~/routes/root/prequal-first-login.js';
import { Sel, STagNew } from '#~/stag/index.js';
import { Anchor } from '@studio/design-system/button';
import type { ReactElement } from 'react';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

export const PostAuthPrequal = (): ReactElement => {
  useRenderedBootHumanWait(true);

  const navigate = useNavigate();
  const { track } = useAnalytics();
  const user = useDepot((µ) => µ.user);
  const produce = useDepot((µ) => µ.produce);

  // ENG-4175: dismiss the BootOverlay on this route. The overlay's `visible`
  // union includes `!isRootReady`, and `isRootReady` is published ONLY by
  // <Root> (the index route). On the first-login bootstrap redirect,
  // use-root-bootstrap navigates here BEFORE isRootReady was ever true, and
  // <Root> unmounts on this non-index route so it stops publishing — leaving
  // the opaque overlay stuck over the wizard. Publish it here on mount so the
  // overlay fades. Mirrors the same fix on the room route (routes/room/room.tsx
  // ~L331). Idempotent (already-true is a no-op), so it adds no re-render churn.
  // Per rules: code-comments-required + reference-rules-in-comments
  // (ai.knowledge/global).
  useEffect(() => {
    produce((draft) => {
      draft.boot.isRootReady = true;
    });
  }, [produce]);

  // Fire STARTED + set the shown-once backstop exactly once, on mount (= the
  // moment the wizard is PROMPTED). React Strict Mode double-invokes effects, so
  // guard with a ref — and only latch once the studio user id is known (the
  // backstop is per-user), so a user that hydrates a tick after mount is not
  // missed. Per knowledge prequal-implementation-lineage: the STARTED/_COMPLETED
  // firing moved here verbatim from register.tsx (event names unchanged).
  const promptedRef = useRef(false);
  useEffect(() => {
    if (promptedRef.current || !user?.id) return;
    promptedRef.current = true;
    markPrequalPrompted(user.id);
    track(EVENT_NAMES.SIGNUP_PREQUALIFICATION_STARTED, EVENT_CATEGORIES.PREQUALIFICATION);
  }, [user?.id, track]);

  // Single exit for both complete and skip — non-blocking guarantee. Returning to
  // '/' re-enters the bootstrap, which (backstop now set) skips the gate and
  // routes to the download/add-camera step.
  const finish = (): void => {
    navigate('/', { replace: true });
  };

  const handleComplete = (answers: PrequalificationAnswers): void => {
    const qualificationBucket = computeQualificationBucket(answers);

    // The segmentation person-property keys persisted on the authenticated user.
    // Recorded in result.yml so post-deploy PostHog checks know exactly what
    // "properties persist" means.
    // Per ENG-4171 — ptz_count replaces camera_available; the platform
    // (device_type) property was removed entirely. This mirrors the same rename
    // #832 applied to the (now-relocated) setCurrentUser persistence block, since
    // this post-auth handler reuses the same PrequalFlow questions/schema.
    const segmentationProps = {
      qualification_bucket: qualificationBucket,
      ptz_count: answers.ptzCount,
      network_match: answers.networkMatch,
      network_type: answers.networkType
    };

    // ORDERING TRAP (prompt §4): the pre-wizard synchronizer read can no longer
    // persist these — it runs at LOGIN, before the wizard has collected answers.
    // So persist them HERE, on the ALREADY-IDENTIFIED user, via $set_once (mirrors
    // the prior setCurrentUser semantics: signup-time attributes never overwritten).
    // Re-identifying with the SAME distinct id keeps the anonymous→identified
    // attribution/alias linkage intact.
    if (user?.id) {
      PostHogManager.getInstance().identify(user.id, undefined, segmentationProps);
    }

    track(
      EVENT_NAMES.SIGNUP_PREQUALIFICATION_COMPLETED,
      EVENT_CATEGORIES.PREQUALIFICATION,
      segmentationProps
    );

    // Clear so answers never leak across users sharing one browser.
    clearPrequalResponses();
    finish();
  };

  // Resume any in-progress answers (the wizard persists per-step to sessionStorage).
  const existing = loadPrequalResponses();

  return (
    <PrequalFlow
      {...(existing !== null ? { initialResponses: existing } : {})}
      onComplete={handleComplete}
      // Explicit non-blocking escape hatch — abandoning still reaches the download
      // step. It MUST go through PrequalFlow's `footer` slot, which forwards to the
      // wizard's persistent chrome; both other placements were measured hidden in
      // the running app (sibling of <PrequalFlow> → top=847 in an 847px viewport
      // under body{overflow-y:hidden}; inside `children` → clipped by the card's
      // overflow-y-auto body at 660px). An unreachable hatch strands a first-login
      // user who does not want to answer, because "Continue" stays disabled until
      // an option is picked — this is their only labelled exit.
      // Per rule: playwright-stag-selectors-only (ai.knowledge/platform) — STag so
      // the e2e can drive skip deterministically.
      footer={
        <Anchor
          className='text-primary'
          onClick={finish}
          {...STagNew(Sel.Prequalification, 'skip')}
        >
          Skip for now
        </Anchor>
      }
    />
  );
};
